import { css } from 'aphrodite';
import moment from 'moment';
import 'moment-timezone';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useCancelMeetingInfoQuery, useCancelMeetingMutation } from '../../queries/CancelMeeting';
import { Button } from '../../../web/components/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { styleSheet as pageStyles } from '../../styles/styles';
import { MeetingContainer } from '../MeetingContainer';
import { styleSheet } from './styles';

export const CancelMeetingPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const [reason, setReason] = React.useState('');
	const [cancelled, setCancelled] = React.useState(false);

	const {
		data: meetingInfo,
		isLoading,
		error: queryError,
	} = useCancelMeetingInfoQuery({
		meetingId: id,
	});

	const {
		mutate: cancelMeeting,
		isPending: isCancelling,
		error: mutationError,
	} = useCancelMeetingMutation({
		onSuccess: () => {
			setCancelled(true);
		},
	});

	const error = queryError?.message || mutationError?.message || null;

	const handleCancelMeeting = () => {
		if (id) {
			cancelMeeting({ meetingId: id, reason });
		}
	};

	const formattedDate = React.useMemo(() => {
		if (!meetingInfo?.startDate) {
			return '';
		}
		const timezone = meetingInfo.timeZoneId || moment.tz.guess();
		return moment(meetingInfo.startDate).tz(timezone).format('MMMM D, YYYY [at] h:mm A z');
	}, [meetingInfo?.startDate, meetingInfo?.timeZoneId]);

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner />
				</div>
			);
		}

		if (error && !meetingInfo) {
			return (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.errorMessage)}>{error}</div>
				</div>
			);
		}

		if (cancelled) {
			return (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.confirmation)}>
						<h2>Meeting Cancelled</h2>
						<p>Your meeting has been cancelled and {meetingInfo?.hostName} has been notified.</p>
						<p>This meeting has been removed from all calendars.</p>
					</div>
				</div>
			);
		}

		return (
			<div className={css(styleSheet.content)}>
				<h2 className={css(styleSheet.title)}>Cancel Meeting</h2>
				<div className={css(styleSheet.meetingDetails)}>
					<p className={css(styleSheet.meetingTitle)}>{meetingInfo?.title}</p>
					<p className={css(styleSheet.hostName)}>with {meetingInfo?.hostName}</p>
					{formattedDate && <span className={css(styleSheet.dateTime)}>{formattedDate}</span>}
				</div>

				{error && <p className={css(styleSheet.errorMessage)}>{error}</p>}

				{meetingInfo?.canCancel ? (
					<>
						<p className={css(styleSheet.warning)}>
							Are you sure you want to cancel this meeting? This action cannot be undone.
						</p>
						<div className={css(styleSheet.reasonContainer)}>
							<label className={css(styleSheet.reasonLabel)} htmlFor='cancellation-reason'>
								Reason for cancellation (optional)
							</label>
							<textarea
								id='cancellation-reason'
								className={css(styleSheet.reasonTextarea)}
								value={reason}
								onChange={e => setReason(e.target.value)}
								placeholder='Let the host know why you need to cancel...'
								rows={3}
								disabled={isCancelling}
							/>
						</div>
						<div className={css(styleSheet.buttonContainer)}>
							<Button
								kind='destructive'
								label={isCancelling ? 'Cancelling...' : 'Cancel Meeting'}
								onClick={handleCancelMeeting}
								disabled={isCancelling}
							/>
						</div>
					</>
				) : (
					<p className={css(styleSheet.errorMessage)}>
						{meetingInfo?.cancelDisabledReason || 'This meeting cannot be cancelled.'}
					</p>
				)}
			</div>
		);
	};

	return (
		<MeetingContainer shortCode=''>
			<div className={css(pageStyles.sharedBackgroundWrapper)}>
				<div className={css(pageStyles.container)}>{renderContent()}</div>
			</div>
		</MeetingContainer>
	);
};
